import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { XMarkIcon, CheckCircleIcon, EyeIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { getDocumentAccess, updateDocumentAccess } from "../../../api/hse/documents";
import { toast } from "react-hot-toast";

/**
 * DocumentAccessDialog - Admin dialog for managing document access
 * Shows users who have read/approved the document and allows managing access
 */
export const DocumentAccessDialog = ({ documentId, open, onClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [accessInfo, setAccessInfo] = useState(null);
  const [accessType, setAccessType] = useState("all"); // 'all' or 'specific'
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && documentId) {
      fetchAccessInfo();
    }
  }, [open, documentId]);

  const fetchAccessInfo = async () => {
    try {
      setLoading(true);
      const data = await getDocumentAccess(documentId);
      setAccessInfo(data);
      
      // Set initial state based on existing access (prefer server access_scope — length match is unreliable)
      if (data && data.users_with_access && data.all_company_users) {
        const scope = data.access_scope;
        if (scope === "all" || (scope == null && data.users_with_access.length === data.all_company_users.length)) {
          setAccessType("all");
          setSelectedUserIds([]);
        } else {
          setAccessType("specific");
          setSelectedUserIds(data.users_with_access.map((u) => u.user.id));
        }
      }
    } catch (err) {
      console.error("Error fetching access info:", err);
      toast.error(t("Kunne ikke laste tilgangsinformasjon"));
      setAccessInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // All selected users get both read and approve access automatically
      await updateDocumentAccess(documentId, {
        access_type: accessType,
        user_ids: accessType === "specific" ? selectedUserIds : []
      });
      toast.success(t("Tilgang oppdatert"));
      fetchAccessInfo(); // Refresh
      onClose();
    } catch (err) {
      console.error("Error updating access:", err);
      toast.error(t("Kunne ikke oppdatere tilgang"));
    } finally {
      setSaving(false);
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  if (!open) return null;

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={t("Administrer tilgang")}
      size="lg"
    >
      {loading ? (
        <div className="p-8 text-center">
          <div className="text-gray-500">{t("Laster...")}</div>
        </div>
      ) : accessInfo && accessInfo.users_read !== undefined ? (
        <div className="space-y-6">
            {/* Users who have read */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                <EyeIcon className="h-5 w-5 mr-2 text-blue-500" />
                {t("Brukere som har lest")} ({accessInfo.users_read?.length || 0})
              </h4>
              <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                {accessInfo.users_read && accessInfo.users_read.length > 0 ? (
                  <ul className="space-y-1">
                    {accessInfo.users_read.map((item, idx) => (
                      <li key={idx} className="text-sm text-gray-700">
                        {item.user?.displayTitle || item.user?.name} - {item.read_at ? new Date(item.read_at).toLocaleDateString() : ''}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">{t("Ingen brukere har lest dokumentet ennå")}</p>
                )}
              </div>
            </div>

            {/* Users who have marked as read/understood */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center">
                <CheckCircleIcon className="h-5 w-5 mr-2 text-green-500" />
                {t("Brukere som har markert som lest/forstått")} ({accessInfo.users_approved?.length || 0})
              </h4>
              <div className="bg-gray-50 rounded-lg p-3 max-h-32 overflow-y-auto">
                {accessInfo.users_approved && accessInfo.users_approved.length > 0 ? (
                  <ul className="space-y-1">
                    {accessInfo.users_approved.map((item, idx) => (
                      <li key={idx} className="text-sm text-gray-700">
                        {item.user?.displayTitle || item.user?.name} - {item.approved_at ? new Date(item.approved_at).toLocaleDateString() : ''}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">{t("Ingen brukere har markert dokumentet som lest/forstått ennå")}</p>
                )}
              </div>
            </div>

          {/* Access control */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
              <UserGroupIcon className="h-5 w-5 mr-2 text-indigo-500" />
              {t("Tilgangskontroll")}
            </h4>

            {/* Access type selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("Velg tilgangstype")}
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="all"
                    checked={accessType === "all"}
                    onChange={(e) => setAccessType(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{t("Alle ansatte")}</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="specific"
                    checked={accessType === "specific"}
                    onChange={(e) => setAccessType(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">{t("Spesifikke ansatte")}</span>
                </label>
              </div>
            </div>

            {/* User selection for specific access */}
            {accessType === "specific" && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("Velg ansatte")}
                </label>
                <div className="bg-gray-50 rounded-lg p-3 max-h-48 overflow-y-auto border border-gray-200">
                  {accessInfo.all_company_users?.map((user) => (
                    <label key={user.id} className="flex items-center py-2 hover:bg-gray-100 px-2 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(user.id)}
                        onChange={() => toggleUserSelection(user.id)}
                        className="mr-3"
                      />
                      <span className="text-sm text-gray-700">{user.name || user.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                {t("Alle valgte brukere får automatisk tilgang til å lese og markere dokumentet som lest/forstått.")}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {t("Avbryt")}
            </button>
            <button
              onClick={handleSave}
              disabled={saving || (accessType === "specific" && selectedUserIds.length === 0)}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? t("Lagrer...") : t("Lagre")}
            </button>
          </div>
        </div>
      ) : (
        <div className="p-8 text-center">
          <div className="text-gray-500">{t("Kunne ikke laste tilgangsinformasjon")}</div>
        </div>
      )}
    </PMDialog>
  );
};

